import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import VideoGreetingModal from './VideoGreetingModal';

interface VideoGreetingWrapperProps {
  children: React.ReactNode;
}

const VideoGreetingWrapper: React.FC<VideoGreetingWrapperProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { selectedClient } = useClient();
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const lastUserRef = useRef<string | null>(null);
  const hasShownRef = useRef(false);
  const [, setIsCheckingVideo] = useState(false);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);

  useEffect(() => {
    const checkAndShowVideo = async () => {
      // Reset if user changed (new login)
      if (currentUser && currentUser.uid !== lastUserRef.current) {
        lastUserRef.current = currentUser.uid;
        hasShownRef.current = false;
        setShowWelcomeScreen(true); // Show welcome screen immediately
      }

      // Only proceed if user is logged in and has selected a client
      if (!currentUser || !selectedClient?.clientId) {
        setShowWelcomeScreen(false);
        return;
      }

      // Don't show again if already shown for this user session
      if (hasShownRef.current) {
        setShowWelcomeScreen(false);
        return;
      }

      setIsCheckingVideo(true);
      try {
        // Fetch the client document directly from Firestore to get the welcomeVideo
        const clientDoc = await getDoc(doc(db, 'clients', selectedClient.clientId));
        
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          
          if (clientData.welcomeVideo) {
            setVideoUrl(clientData.welcomeVideo);
            setShowVideoModal(true);
            hasShownRef.current = true;
          } else {
            setShowWelcomeScreen(false);
          }
        }
      } catch (error) {
        console.error('Error fetching client data:', error);
        setShowWelcomeScreen(false);
      } finally {
        setIsCheckingVideo(false);
        // Hide welcome screen after a delay if no video
        if (!showVideoModal) {
          setTimeout(() => setShowWelcomeScreen(false), 500);
        }
      }
    };

    checkAndShowVideo();
  }, [currentUser, selectedClient, showVideoModal]);

  const handleCloseVideo = () => {
    setShowVideoModal(false);
    setShowWelcomeScreen(false);
  };

  // Show welcome screen while checking for video
  if (showWelcomeScreen && !showVideoModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-blue-50">
        <div className="text-center">
          <div className="mb-6">
            <img src="/logo_pulpou.svg" alt="Pulpou" className="h-16 mx-auto" />
          </div>
          <div className="animate-pulse">
            <div className="h-2 w-32 bg-gray-200 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <VideoGreetingModal 
        isOpen={showVideoModal}
        onClose={handleCloseVideo}
        videoUrl={videoUrl}
      />
    </>
  );
};

export default VideoGreetingWrapper;